import type { ReactNode } from 'react';
import { NavLink } from 'react-router';
import { useI18n } from '../i18n';

// Material's list (TF-95): what the record lists become on a phone, where a table would hide most
// of its columns. One-, two- or three-line items (56, 72, 88 high): a leading element, a headline,
// supporting text, and a trailing element. An item that opens something is one tab stop - its
// headline - with the whole row as the target and the state layer.

export function List({ label, className, children }: { label: string; className?: string; children: ReactNode }) {
  return (
    <ul className={className ? `m3-list ${className}` : 'm3-list'} aria-label={label}>
      {children}
    </ul>
  );
}

export function ListItem({
  leading,
  headline,
  supporting,
  third,
  trailing,
  to,
  onOpen,
  selected,
  className,
}: {
  /** An avatar, icon, thumbnail or checkbox. */
  leading?: ReactNode;
  headline: ReactNode;
  supporting?: ReactNode;
  /** A third line: tags, a stage, a date. */
  third?: ReactNode;
  /** Text, an icon, or an icon button (above the row's own target). */
  trailing?: ReactNode;
  /** The whole row opens this address... */
  to?: string;
  /** ...or does this. */
  onOpen?: () => void;
  selected?: boolean;
  className?: string;
}) {
  const lines = 1 + (supporting ? 1 : 0) + (third ? 1 : 0);
  const opens = Boolean(to || onOpen);
  const classes = ['m3-item', opens && 'm3-item-open', selected && 'selected', className].filter(Boolean).join(' ');
  return (
    <li className={classes} data-lines={lines}>
      {leading && <span className="m3-item-leading">{leading}</span>}
      <span className="m3-item-content">
        <span className="m3-item-headline">
          {to ? (
            // A NavLink: the row of the record open beside the list says so (aria-current, TF-98).
            <NavLink to={to} className="m3-item-link">
              {headline}
            </NavLink>
          ) : onOpen ? (
            <button type="button" className="m3-item-link" onClick={onOpen}>
              {headline}
            </button>
          ) : (
            headline
          )}
        </span>
        {supporting && <span className="m3-item-supporting">{supporting}</span>}
        {third && <span className="m3-item-third">{third}</span>}
      </span>
      {trailing && <span className="m3-item-trailing">{trailing}</span>}
    </li>
  );
}

/** Parts of a line, joined by a middle dot; the empty ones left out. */
export function dotted(...parts: (ReactNode | null | undefined | false)[]): ReactNode {
  const kept = parts.filter((part): part is Exclude<ReactNode, null | undefined | false | ''> => part !== null && part !== undefined && part !== false && part !== '');
  return kept.map((part, index) => (
    <span key={index}>
      {index > 0 && <span aria-hidden> · </span>}
      {part}
    </span>
  ));
}

/** While a list loads (TF-95): three items' shapes, the same everywhere, instead of a spinner. */
export function ListSkeleton({ rows = 3 }: { rows?: number }) {
  const { t } = useI18n();
  return (
    <ul className="m3-list list-skeleton" aria-busy="true" aria-label={t('common.loading')}>
      {Array.from({ length: rows }, (_, index) => (
        <li key={index} className="m3-item" data-lines={2}>
          <span className="m3-item-leading skeleton-block skeleton-round" />
          <span className="m3-item-content">
            <span className="skeleton-block skeleton-line" />
            <span className="skeleton-block skeleton-line skeleton-short" />
          </span>
        </li>
      ))}
    </ul>
  );
}
